import { Decimal } from "./decimal.js";
import { Money, roundPreservingSum } from "./money.js";
function assertRate(ratePercent) {
    const rate = Decimal.from(ratePercent);
    if (rate.isNegative())
        throw new RangeError(`KDV oranı negatif olamaz (${rate.toString()})`);
    return rate;
}
/** KDV DAHİL tutardan matrah ve KDV ayırır. net = gross / (1 + r), kuruşa yuvarlanır; KDV = gross − net. */
export function splitVatInclusive(gross, ratePercent) {
    const rate = assertRate(ratePercent);
    const divisor = Decimal.ONE.add(rate.div(100));
    const net = Money.fromDecimalMajor(gross.toDecimal().div(divisor));
    return { net, vat: gross.sub(net), gross };
}
/** KDV HARİÇ tutara KDV ekler. KDV = net × r, kuruşa yuvarlanır. */
export function addVat(net, ratePercent) {
    const vat = net.percentage(assertRate(ratePercent));
    return { net, vat, gross: net.add(vat) };
}
/**
 * Belge KDV hesabı — yuvarlama farkının belgeye özgü, DETERMİNİSTİK dağıtımı.
 *
 * Sorun: satır satır yuvarlanan KDV'lerin toplamı, oran grubunun toplam matrahı üzerinden hesaplanan KDV'den
 * birkaç kuruş sapabilir; e-belgede ise oran bazındaki toplam (TaxSubtotal) esas alınır.
 * Çözüm: KDV önce ORAN GRUBU toplamında hesaplanır, sonra satırlara "en büyük kalan" yöntemiyle dağıtılır.
 * Böylece (1) her satırda net + KDV = brüt, (2) satır toplamları grup ve belge toplamlarına kuruşu kuruşuna eşittir,
 * (3) aynı belge her hesaplandığında aynı sonucu verir (eşitlikte satır sırası belirleyicidir).
 */
export function calculateVatDocument(lines, mode) {
    if (lines.some((line) => line.amount.isNegative())) {
        throw new RangeError("calculateVatDocument: satır tutarı negatif olamaz; iade için ayrı belge düzenlenir");
    }
    const result = new Array(lines.length);
    const groups = new Map();
    lines.forEach((line, index) => {
        const rate = assertRate(line.ratePercent);
        const key = rate.toFixed(4);
        const group = groups.get(key) ?? { rate, indexes: [] };
        group.indexes.push(index);
        groups.set(key, group);
    });
    const byRate = [];
    for (const [key, group] of [...groups.entries()].sort(([, a], [, b]) => a.rate.cmp(b.rate))) {
        const amounts = group.indexes.map((index) => lines[index].amount);
        const groupAmount = Money.sum(amounts);
        const factor = group.rate.div(100);
        if (mode === "VAT_INCLUSIVE") {
            // Brüt sabittir (müşterinin ödediği). Grup matrahı hesaplanır, satırlara kesin matrahlarına göre dağıtılır.
            const groupNet = splitVatInclusive(groupAmount, group.rate).net;
            const exactNets = amounts.map((amount) => Decimal.from(amount.minor).div(Decimal.ONE.add(factor)));
            const nets = roundPreservingSum(exactNets, groupNet.minor);
            group.indexes.forEach((lineIndex, position) => {
                const gross = amounts[position];
                const net = Money.fromMinor(nets[position]);
                result[lineIndex] = { net, vat: gross.sub(net), gross };
            });
            byRate.push({ ratePercent: key, net: groupNet, vat: groupAmount.sub(groupNet), gross: groupAmount });
        }
        else {
            // Matrah sabittir. Grup KDV'si hesaplanır, satırlara kesin KDV'lerine göre dağıtılır.
            const groupVat = groupAmount.percentage(group.rate);
            const exactVats = amounts.map((amount) => Decimal.from(amount.minor).mul(factor));
            const vats = roundPreservingSum(exactVats, groupVat.minor);
            group.indexes.forEach((lineIndex, position) => {
                const net = amounts[position];
                const vat = Money.fromMinor(vats[position]);
                result[lineIndex] = { net, vat, gross: net.add(vat) };
            });
            byRate.push({ ratePercent: key, net: groupAmount, vat: groupVat, gross: groupAmount.add(groupVat) });
        }
    }
    return {
        lines: result,
        byRate,
        totals: {
            net: Money.sum(byRate.map((entry) => entry.net)),
            vat: Money.sum(byRate.map((entry) => entry.vat)),
            gross: Money.sum(byRate.map((entry) => entry.gross)),
        },
    };
}
//# sourceMappingURL=vat.js.map